import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Sparkles } from 'lucide-react';
import { usePeriod } from './PeriodContext';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { selectedPeriod, setSelectedPeriod } = usePeriod();
  const location = useLocation();

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsPeriodDropdownOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const periods = ['2025-2026', '2026-2027'];
  const showPeriodSelector = location.pathname !== '/about' && location.pathname !== '/daftar2027';

  const navLinks = [
    { to: '/', label: 'Beranda' },
    { to: '/about', label: 'Tentang' },
    { to: '/kegiatan', label: 'Kegiatan' },
    { to: '/contact', label: 'Hubungi Kami' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-slate-950/85 backdrop-blur-xl border-b border-slate-800/80 shadow-2xl shadow-indigo-950/20 py-3' 
          : 'bg-slate-950/60 backdrop-blur-md border-b border-slate-800/40 py-4'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between">

        {/* Logo / Brand Name */}
        <Link 
          to="/" 
          onClick={closeMenu} 
          className="flex items-center gap-3 z-50 group focus:outline-none"
        >
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl blur opacity-40 group-hover:opacity-75 transition duration-300"></div>
            <img 
              src="/favicon.ico" 
              alt="FKPO Logo" 
              className="relative w-8 h-8 sm:w-9 sm:h-9 rounded-xl object-cover border border-indigo-400/30 shadow-md group-hover:scale-105 transition-transform" 
            />
          </div>
          <div className="flex flex-col">
            <span className="text-base sm:text-lg font-bold tracking-tight text-white flex items-center gap-1.5 leading-none">
              FKPO <span className="text-indigo-400 font-extrabold">KP</span>
            </span>
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-medium hidden sm:inline leading-none mt-1">
              Kulon Progo
            </span>
          </div>
        </Link>

        {/* --- DESKTOP NAVIGATION --- */}
        <nav className="hidden md:flex gap-1 lg:gap-2 text-sm font-medium items-center bg-slate-900/60 border border-slate-800/80 p-1.5 rounded-full backdrop-blur-md">
          {navLinks.map((link) => {
            const active = isActive(link.to);
            return (
              <Link 
                key={link.to}
                to={link.to} 
                className={`relative px-4 py-1.5 rounded-full transition-all duration-200 ${
                  active 
                    ? 'text-white font-semibold bg-indigo-600/90 shadow-md shadow-indigo-600/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/60'
                }`}
              >
                {link.label}
              </Link>
            );
          })}

          {/* Period Selector Dropdown */}
          {showPeriodSelector && (
            <div className="relative ml-1 pl-2 border-l border-slate-800">
              <button
                onClick={() => setIsPeriodDropdownOpen(!isPeriodDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800/80 hover:bg-slate-700/80 text-slate-200 hover:text-white rounded-full transition-all text-xs border border-slate-700/60"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span>{selectedPeriod}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isPeriodDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isPeriodDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-36 bg-slate-900/95 backdrop-blur-xl border border-slate-700/80 rounded-2xl shadow-2xl p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150">
                  <div className="text-[10px] uppercase font-bold text-slate-400 px-3 py-1 tracking-wider">
                    Pilih Periode
                  </div>
                  {periods.map((period) => (
                    <button
                      key={period}
                      onClick={() => {
                        setSelectedPeriod(period);
                        setIsPeriodDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded-xl transition-colors flex items-center justify-between ${
                        selectedPeriod === period 
                          ? 'bg-indigo-600/20 text-indigo-300 font-semibold border border-indigo-500/30' 
                          : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
                      }`}
                    >
                      <span>{period}</span>
                      {selectedPeriod === period && <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* --- DESKTOP CTA BUTTON --- */}
        <div className="hidden md:flex items-center gap-3">
          <Link 
            to="/daftar2027" 
            className="group relative inline-flex items-center gap-1.5 bg-gradient-to-r from-indigo-500 via-indigo-600 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white text-xs px-5 py-2.5 rounded-full font-semibold transition-all shadow-lg shadow-indigo-600/25 hover:shadow-indigo-600/40 hover:scale-[1.02] active:scale-95"
          >
            <Sparkles className="w-3.5 h-3.5 text-indigo-200 group-hover:rotate-12 transition-transform" />
            <span>Gabung Kami</span>
          </Link>
        </div>

        {/* --- MOBILE MENU TOGGLE BUTTON --- */}
        <button
          className="md:hidden p-2 rounded-xl bg-slate-900/80 border border-slate-800 text-slate-300 hover:text-white focus:outline-none transition-colors"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle navigation menu"
        >
          {isMenuOpen ? <X className="w-5 h-5 text-indigo-400" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* --- MOBILE NAVIGATION MENU (Drawer) --- */}
      <div 
        className={`md:hidden fixed inset-x-0 top-[60px] bg-slate-950/95 backdrop-blur-2xl border-b border-slate-800/80 transition-all duration-300 shadow-2xl ${
          isMenuOpen 
            ? 'opacity-100 translate-y-0 pointer-events-auto max-h-[85vh] overflow-y-auto' 
            : 'opacity-0 -translate-y-4 pointer-events-none max-h-0 overflow-hidden'
        }`}
      >
        <nav className="p-6 space-y-3">
          {navLinks.map((link) => {
            const active = isActive(link.to);
            return (
              <Link 
                key={link.to}
                to={link.to} 
                onClick={closeMenu} 
                className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                  active 
                    ? 'bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 font-semibold' 
                    : 'text-slate-300 hover:bg-slate-900/60 hover:text-white'
                }`}
              >
                <span>{link.label}</span>
                {active && <span className="w-2 h-2 rounded-full bg-indigo-400"></span>}
              </Link>
            );
          })}

          {/* Mobile Period Selector */}
          {showPeriodSelector && (
            <div className="pt-3 pb-1 border-t border-slate-800/80">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 px-1">
                Pilih Periode Aktif
              </div>
              <div className="grid grid-cols-2 gap-2">
                {periods.map((period) => (
                  <button
                    key={period}
                    onClick={() => {
                      setSelectedPeriod(period);
                    }}
                    className={`py-2 px-3 rounded-xl text-xs font-semibold transition-all border ${
                      selectedPeriod === period
                        ? 'bg-indigo-600 text-white border-indigo-500 shadow-lg shadow-indigo-600/30'
                        : 'bg-slate-900/80 text-slate-300 border-slate-800 hover:bg-slate-800'
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2">
            <Link 
              to="/daftar2027" 
              onClick={closeMenu} 
              className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold py-3 rounded-xl shadow-lg shadow-indigo-600/25 active:scale-95 transition-transform text-sm"
            >
              <Sparkles className="w-4 h-4" />
              <span>Gabung Kami (Oprec 2027)</span>
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
}

export default Header;

